import { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Camera } from 'lucide-react'
import { Casilla, Tabla, useFilaEnlace } from '../kit'
import { Bien } from '../../types/inventario'
import { cuantasFotos, marcaModelo } from '../../utils/inventario'
import { BadgesBien } from './BadgesBien'

/**
 * Bienes en una tabla apilable (tarjetas en el celular). En una oficina se pueden
 * marcar filas (`seleccion`) y resaltar las que encontró la revisión por N°; en la
 * lista general se muestra además el recinto. Bajo el N°, cuántas fotos tiene. Toda la
 * fila lleva a la ficha del bien. `columnas` agrega columnas propias de una
 * vista (en Bajas: cuándo y por qué), antes del estado; `sin` quita las que ahí sobran.
 */
export function TablaBienes<B extends Bien>({
  bienes, etiqueta, mostrarRecinto = false, seleccion, onSeleccion, resaltados, acciones, columnas = [], sin = [],
}: {
  bienes: B[]
  etiqueta: string
  mostrarRecinto?: boolean
  seleccion?: Set<number>
  onSeleccion?: (ids: Set<number>) => void
  resaltados?: Set<number>
  acciones?: (bien: B) => ReactNode
  columnas?: { titulo: string; celda: (bien: B) => ReactNode }[]
  sin?: ('serie' | 'estado')[]
}) {
  const con = (columna: 'serie' | 'estado') => !sin.includes(columna)
  const fila = useFilaEnlace() // toda la fila lleva a la ficha del bien
  const conSeleccion = !!seleccion && !!onSeleccion
  const todos = conSeleccion && bienes.length > 0 && bienes.every((b) => seleccion?.has(b.id))
  const alternar = (id: number) => {
    if (!seleccion || !onSeleccion) return
    const nueva = new Set(seleccion)
    if (nueva.has(id)) nueva.delete(id)
    else nueva.add(id)
    onSeleccion(nueva)
  }

  return (
    <Tabla etiqueta={etiqueta} apilable>
      <thead>
        <tr>
          {conSeleccion && (
            <th scope="col" className="tabla__casilla">
              <Casilla
                etiqueta="Marcar todos"
                etiquetaOculta
                checked={todos}
                onChange={() => onSeleccion?.(todos ? new Set() : new Set(bienes.map((b) => b.id)))}
              />
            </th>
          )}
          <th scope="col">N°</th>
          <th scope="col">Descripción</th>
          {con('serie') && <th scope="col">N° de serie</th>}
          {mostrarRecinto && <th scope="col">Recinto</th>}
          {columnas.map((c) => (
            <th key={c.titulo} scope="col">{c.titulo}</th>
          ))}
          {con('estado') && <th scope="col">Estado</th>}
          {acciones && <th scope="col"><span className="visualmente-oculto">Acciones</span></th>}
        </tr>
      </thead>
      <tbody>
        {bienes.map((b) => (
          <tr
            key={b.id}
            id={`bien-${b.id}`}
            {...fila(`/app/bienes/${b.id}`)}
            className={`fila--enlace${resaltados?.has(b.id) ? ' fila--resaltada' : ''}`}
          >
            {conSeleccion && (
              <td className="tabla__casilla">
                <Casilla etiqueta={`Marcar ${b.numero || b.descripcion}`} etiquetaOculta checked={!!seleccion?.has(b.id)} onChange={() => alternar(b.id)} />
              </td>
            )}
            <td className="tabla__principal">
              <Link to={`/app/bienes/${b.id}`} className="tabla__enlace">{b.numero || 'Sin N°'}</Link>
              <span className={`tabla__sub fotos-cuenta${b.n_fotos ? '' : ' fotos-cuenta--ninguna'}`}>
                <Camera className="dcc-i" size={13} aria-hidden />
                {cuantasFotos(b.n_fotos)}
              </span>
            </td>
            <td data-etiqueta="Descripción" className="tabla__texto">
              <div>
                {b.descripcion}
                {marcaModelo(b) && <span className="tabla__sub">{marcaModelo(b)}</span>}
              </div>
            </td>
            {con('serie') && <td data-etiqueta="Serie">{b.serie || '—'}</td>}
            {mostrarRecinto && (
              <td data-etiqueta="Recinto" className="tabla__texto">
                <div>
                  {b.recinto ? <Link to={`/app/oficinas/${b.recinto.codigo}`}>{b.recinto.nombre}</Link> : 'Sin ubicación'}
                  {b.recinto && <span className="tabla__sub">{b.recinto.codigo}</span>}
                </div>
              </td>
            )}
            {columnas.map((c) => (
              <td key={c.titulo} data-etiqueta={c.titulo} className="tabla__texto">
                <div>{c.celda(b)}</div>
              </td>
            ))}
            {con('estado') && (
              <td data-etiqueta="Estado">
                <BadgesBien bien={b} />
              </td>
            )}
            {acciones && <td className="tabla__acciones">{acciones(b)}</td>}
          </tr>
        ))}
      </tbody>
    </Tabla>
  )
}
